import { z } from 'zod';
import { apiGet, apiPost } from '@/lib/api';

/** Stepper 6 etapów. W v1 osiągalny jest tylko pierwszy — resztę przestawia agent. */
export const CHANGE_STATUSES = ['QUEUED', 'SENT', 'APPLIED', 'CONFIRMED', 'FAILED', 'SUPERSEDED'] as const;
export type ChangeStatus = (typeof CHANGE_STATUSES)[number];

/** Kolejność steppera. `FAILED`/`SUPERSEDED` to zakończenia poza torem, nie kolejne etapy. */
export const STEPPER_PATH = ['QUEUED', 'SENT', 'APPLIED', 'CONFIRMED'] as const;

const ChangeRowSchema = z.object({
  seq: z.string(),
  tableName: z.string(),
  action: z.string(),
  pk: z.unknown(),
  payload: z.unknown(),
});

const OperationSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.enum(CHANGE_STATUSES),
  source: z.enum(['MODULE', 'AUTHORING']),
  /**
   * SPRAWCA Z ZEWNĄTRZ (`/api/v1`, dziś Vending+) — `createdBy` jest wtedy puste.
   * Format z serwera: `osoba (etykieta tokenu)`.
   */
  externalActor: z.string().nullable(),
  authorNote: z.string().nullable(),
  error: z.string().nullable(),
  /**
   * ZASTRZEŻENIE DO UDANEJ OPERACJI (kolumny zdjęte przez agenta, bo baza ich nie ma).
   *
   * ⚠️ `nullish`, nie `nullable`: instancja starsza od tej zmiany tego pola nie odda,
   * a schemat ostrzejszy od odpowiedzi zamienia UDANY odczyt w błąd ekranu — dokładnie
   * to zdarzyło się 10.09.2026 przy wymianie indeksu.
   */
  uwagi: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
  confirmedAt: z.string().nullable(),
  createdBy: z.object({ email: z.string(), name: z.string().nullable() }).nullable(),
  changes: z.array(ChangeRowSchema),
  tables: z.array(z.string()),
  /**
   * WYDANIA PER WĘZEŁ — kto tę operację dostał i co z nią zrobił.
   *
   * ⚠️ ISTNIEJE, BO W SMART SYNCU „POTWIERDZONA" ZNACZY „WSZYSTKIE BAZY". Do 25.08.2026 operacja
   * szła do węzła, który zapytał pierwszy, i jego meldunek zamykał ją dla całej wyspy — czyli
   * ekran mówił „potwierdzona", gdy zmianę miała jedna z czterech baz. Bez tej listy „w toku"
   * nie mówi, NA CO się czeka.
   *
   * `optional`, bo w trybie `ZWYKLY` wydań nie prowadzimy (jedna fizyczna baza) i instancja
   * starsza od tej zmiany pola nie odda.
   */
  wydania: z
    .array(
      z.object({
        nodeId: z.string(),
        status: z.enum(CHANGE_STATUSES),
        sentAt: z.string(),
        confirmedAt: z.string().nullable(),
        error: z.string().nullable(),
        applied: z.number(),
        skipped: z.number(),
      }),
    )
    .optional(),

  /**
   * SKWITOWANIE NIEUDANEJ OPERACJI — kto i kiedy ją przejrzał, oraz ile razy ją ponawiano.
   *
   * ⚠️ `optional`, BO INSTANCJA MOŻE BYĆ STARSZA OD TEJ ZMIANY. Ekran musi znieść brak pola bez
   * awarii — ta sama reguła, którą stosują `wydania` wyżej.
   */
  zamknietaO: z.string().nullable().optional(),
  zamknietaPrzez: z.string().nullable().optional(),
  ponowionaO: z.string().nullable().optional(),
  ponowionaPrzez: z.string().nullable().optional(),
  ponowien: z.number().optional(),
});

export type Operation = z.infer<typeof OperationSchema>;

const ListSchema = z.object({
  rows: z.array(OperationSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export type ChangesQuery = {
  groupId: string;
  status?: ChangeStatus;
  source?: 'MODULE' | 'AUTHORING';
  tableName?: string;
  search?: string;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
};

/**
 * AUTOR Z ZEWNĄTRZ (`/api/v1`, dziś Vending+) — `osoba (etykieta tokenu)` albo `null` dla operacji z portalu.
 *
 * ⚠️ JEDNA REGUŁA DLA LISTY I SZCZEGÓŁÓW: konto portalu ma pierwszeństwo, a plakietka „z zewnątrz” stoi
 * tylko tam, gdzie konta nie ma. Na liście operacja z Vending+ wyglądała dotąd jak nasza — rozróżniało
 * je dopiero otwarcie szczegółów (06.10.2026).
 */
export const autorZZewnatrz = (op: { createdBy: unknown; externalActor: string | null }): string | null =>
  op.createdBy === null && op.externalActor !== null ? op.externalActor : null;

export const fetchChanges = (q: ChangesQuery) => {
  const p = new URLSearchParams({
    groupId: q.groupId,
    page: String(q.page),
    pageSize: String(q.pageSize),
    sort: q.sort,
    dir: q.dir,
  });
  if (q.status) p.set('status', q.status);
  if (q.source) p.set('source', q.source);
  if (q.tableName) p.set('tableName', q.tableName);
  if (q.search) p.set('search', q.search);
  return apiGet(`/changes?${p.toString()}`, ListSchema);
};

const SummarySchema = z.object({
  /**
   * DLACZEGO KOLEJKA STOI — `null` = nic jej nie wstrzymuje.
   *
   * ⚠️ `optional`, bo instancja starsza od tej zmiany pola nie odda, a ekran zmian ma się wtedy
   * pokazać bez banera, a nie wywalić na walidacji.
   */
  wstrzymanie: z
    .object({
      /**
       * ⚠️⚠️ `string`, NIE `enum`, i to jest naprawa policzonego błędu, a nie rozluźnienie. Ekran
       * NIE ROZGAŁĘZIA SIĘ po tym polu — wypisuje `zdanie` i `wezly`. Wyliczenie w kliencie
       * znaczyłoby więc tylko tyle, że dołożenie serwerowi nowego powodu wywraca CAŁY baner na
       * walidacji: zamiast nowego zdania człowiek dostaje pusty ekran. Dokładnie ten wzorzec
       * („zod w kliencie ostrzejszy niż serwis") zjadł już raz udany zapis pokazany jako błąd.
       */
      powod: z.string(),
      zdanie: z.string(),
      wezly: z.array(z.string()),
      operacji: z.number(),
    })
    .nullable()
    .optional(),
  statuses: z.record(z.string(), z.number()),
  /**
   * NIEUDANE, KTORYCH NIKT JESZCZE NIE PRZEJRZAL.
   *
   * ⚠️⚠️ TO JEST LICZBA, KTORA DECYDUJE, CZY JEST CO ROBIC — a nie `statuses.FAILED`.
   * Rozklad statusow o skwitowaniu nie wie, wiec ekran liczacy z niego pokazywal te same piec
   * porazek i po ich zamknieciu (zgloszenie 04.09.2026: „wyskakuje 0 i nic sie nie dzieje").
   *
   * ⚠️ Z `.catch(0)`, bo starsza instancja tego pola nie oddaje: portal i API jada osobnymi
   * obrazami i przez chwile po wydaniu nowy ekran rozmawia ze starym API. Brak pola nie moze
   * wywalac calego podsumowania kolejki.
   */
  nieudaneOtwarte: z.number().catch(0),
  sources: z.record(z.string(), z.number()),
  pending: z.number(),
  changeCount: z.number(),
  /** Zmiany w oczekujących operacjach. `optional`: starsza instancja tego pola nie odda. */
  pendingChangeCount: z.number().optional(),
  tables: z.array(z.object({ tableName: z.string(), count: z.number() })),
  /** Zmiany w oczekujących po tabelach — do listy zwijania. `optional`: starsza instancja tego pola nie odda. */
  pendingTables: z.array(z.object({ tableName: z.string(), count: z.number() })).optional(),
  oldestPending: z.object({ id: z.string(), title: z.string(), createdAt: z.string() }).nullable(),
  nothingEverAdvanced: z.boolean(),
});

export type ChangesSummary = z.infer<typeof SummarySchema>;

export const fetchSummary = (groupId: string) => apiGet(`/changes/summary?groupId=${groupId}`, SummarySchema);

/** Stan wiersza sprzed zmiany od jednej maszyny (15.10.2026) — pusto przy starszych operacjach i agentach. */
const StanPrzedSchema = z.object({
  wezel: z.string(),
  maszyna: z.string().nullish(),
  istnial: z.boolean(),
  wiersz: z.record(z.string(), z.unknown()).nullable(),
});

const DetailSchema = OperationSchema.omit({ tables: true }).extend({
  changes: z.array(ChangeRowSchema.extend({ createdAt: z.string(), przed: z.array(StanPrzedSchema).default([]) })),
  /** Którą operację ta cofa / która ją cofnęła — starsze API tych pól nie oddaje. */
  cofaOpId: z.string().nullish(),
  cofnietaPrzez: z.object({ id: z.string(), title: z.string(), status: z.string(), createdAt: z.string() }).nullish(),
});

export const fetchOperation = (opId: string, groupId: string) =>
  apiGet(`/changes/${opId}?groupId=${groupId}`, DetailSchema);

/** Ile pełnych dni czeka operacja. Kolejka, której nikt nie opróżnia, rośnie — to jedyny sygnał. */
export function waitingDays(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/**
 * CZYNNOŚCI NA NIEUDANYCH OPERACJACH.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO ZE ZGŁOSZENIA Z PRODUKCJI PESA (04.09.2026): pulpit pokazywał „wymaga uwagi —
 *  37 nieudanych operacji", a portal nie miał czym ich skwitować. Cytat: „nie wiem co to za
 *  operacje nieudane ani jak je potwierdzić/anulować czy skasować błąd czy cokolwiek".
 *
 *  ⚠️ NADAL NIE MA ANULOWANIA OPERACJI CZEKAJĄCEJ — tę opróżnia agent, a wyjmowanie jej z kolejki
 *  byłoby cichym gubieniem zmian. Zamknąć i ponowić wolno WYŁĄCZNIE nieudaną.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const zamknijOperacje = (opId: string, groupId: string) =>
  apiPost(`/changes/${opId}/zamknij`, { groupId }, z.object({ zamkniete: z.number() }).passthrough());

export const zamknijNieudane = (groupId: string) =>
  apiPost(
    '/changes/zamknij-nieudane',
    { groupId },
    /** `przejrzanych` = ile porazek jest juz skwitowanych — po to, zeby zero mialo powod. */
    z.object({ zamkniete: z.number(), przejrzanych: z.number().catch(0) }),
  );

export const ponowOperacje = (opId: string, groupId: string) =>
  apiPost(
    `/changes/${opId}/ponow`,
    { groupId },
    z.object({ ponowiona: z.boolean(), usunieteWydania: z.number(), ponowien: z.number() }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ZWINIĘCIE OCZEKUJĄCYCH OPERACJI JEDNEJ TABELI — jedyna czynność zdejmująca coś z kolejki.
 *
 *  ⚠️⚠️ `tabela` JEST WYMAGANA I TO NIE JEST FORMALNOŚĆ. Bez niej byłby to jeden przycisk
 *  kasujący kolejkę wyspy. Nazwanie tabeli sprawia, że zasięg czynności widać w chwili jej
 *  zlecania — i tak samo stoi potem w rejestrze zmian.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const zwinOczekujace = (groupId: string, tabela: string) =>
  apiPost('/changes/zwin-oczekujace', { groupId, tabela }, z.object({ zwiniete: z.number(), tabela: z.string() }));
